import { useState, useEffect } from 'react';
import LeadFocusRow from '../LeadFocusRow/LeadFocusRow';
import './LeadFocusView.css';

const TERMINAL_STATUSES = ['CONVERTED', 'DROPPED'];

const TYPE_LABELS = {
  WHATSAPP: 'WhatsApp', MAIL: 'Mail', AUDIO_CALL: 'Audio Call',
  VIDEO_CALL: 'Video Call', ONLINE_MEETING: 'Online Meeting', IN_PERSON_MEETING: 'In Person',
  // legacy keys
  CALL: 'Call', IN_PERSON: 'In Person',
};
const TYPE_ICONS = {
  WHATSAPP: '💬', MAIL: '📧', AUDIO_CALL: '📞',
  VIDEO_CALL: '📹', ONLINE_MEETING: '💻', IN_PERSON_MEETING: '🤝',
  CALL: '📞', IN_PERSON: '🤝',
};
const OUTCOME_GOOD  = ['CONNECTED', 'REPLIED', 'ATTENDED'];
const SOURCE_LABELS = {
  REFERRAL: 'Referral', EVENT: 'Event', WALK_IN: 'Walk-in',
  COLD_CALL: 'Cold call', DIGITAL: 'Digital', OTHER: 'Other'
};
const INTEREST_LABELS = { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High' };

function isOverdue(lead) {
  const now = Date.now();
  if (lead.next_follow_up_at && new Date(lead.next_follow_up_at) < now) return true;
  if (!lead.last_contacted_at && (now - new Date(lead.created_at)) / 86400000 > 3) return true;
  if (lead.last_contacted_at && !lead.next_follow_up_at &&
    (now - new Date(lead.last_contacted_at)) / 86400000 > 3) return true;
  return false;
}

function isDueToday(lead) {
  if (!lead.next_follow_up_at) return false;
  if (isOverdue(lead)) return false;
  const followUp = new Date(lead.next_follow_up_at);
  const today = new Date();
  return followUp.toDateString() === today.toDateString();
}

function isHotNoRecentTouch(lead) {
  if ((lead.hot_score || 0) <= 60) return false;
  if (isOverdue(lead) || isDueToday(lead)) return false;
  const now = Date.now();
  const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
  const noRecentContact = !lead.last_contacted_at || (now - new Date(lead.last_contacted_at)) > threeDaysMs;
  const noFollowUp = !lead.next_follow_up_at;
  return noRecentContact && noFollowUp;
}

function isNewUnactioned(lead) {
  if (lead.status !== 'NEW') return false;
  if (isOverdue(lead) || isDueToday(lead) || isHotNoRecentTouch(lead)) return false;
  if ((lead.touchpoints || []).length > 0) return false;
  const oneDayMs = 24 * 60 * 60 * 1000;
  return (Date.now() - new Date(lead.created_at)) > oneDayMs;
}

function formatRefreshed(ts) {
  if (!ts) return '';
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 10) return 'Just now';
  if (secs < 60) return `${secs}s ago`;
  return `${Math.floor(secs / 60)}m ago`;
}

function relativeDate(iso) {
  if (!iso) return '';
  const diffMs  = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr  = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 2)   return 'Just now';
  if (diffMin < 60)  return `${diffMin}m ago`;
  if (diffHr  < 24)  return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  return `${diffDay} days ago`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function DetailPanel({ lead, onLogClick, onCardClick }) {
  const touchpoints = [...(lead.touchpoints || [])].reverse();

  return (
    <div className="lfv__detail-inner">
      <div className="lfv__detail-header">
        <div className="lfv__detail-name">
          {(lead.hot_score || 0) > 70 && '🔥 '}{lead.name}
        </div>
        <div className="lfv__detail-phone">{lead.phone}</div>
        {lead.email && <div className="lfv__detail-email">{lead.email}</div>}

        <div className="lfv__dchips">
          {lead.source && (
            <span className="lfv__dchip lfv__dchip--source">
              {SOURCE_LABELS[lead.source] || lead.source}
            </span>
          )}
          {lead.interest_level && (
            <span className={`lfv__dchip lfv__dchip--interest lfv__dchip--${lead.interest_level.toLowerCase()}`}>
              {INTEREST_LABELS[lead.interest_level]} interest
            </span>
          )}
          {lead.estimated_aum && (
            <span className="lfv__dchip lfv__dchip--aum">
              ₹{(lead.estimated_aum / 100000).toFixed(0)}L AUM
            </span>
          )}
        </div>
      </div>

      {lead.notes && (
        <div className="lfv__detail-notes">
          <div className="lfv__detail-section-lbl">Notes</div>
          <div className="lfv__detail-notes-text">{lead.notes}</div>
        </div>
      )}

      <div className="lfv__detail-tp-section">
        <div className="lfv__detail-section-lbl">
          Touchpoint History
          <span className="lfv__tp-count">{touchpoints.length}</span>
        </div>

        {touchpoints.length === 0 ? (
          <div className="lfv__no-tp">No touchpoints logged yet</div>
        ) : (
          <div className="lfv__tp-list">
            {touchpoints.map((tp, i) => {
              const good = OUTCOME_GOOD.includes(tp.outcome);
              return (
                <div key={tp.id || i} className="lfv__tp-row">
                  <div className="lfv__tp-icon">{TYPE_ICONS[tp.type] || '📋'}</div>
                  <div className="lfv__tp-body">
                    <div className="lfv__tp-meta">
                      <span className="lfv__tp-type">{TYPE_LABELS[tp.type] || tp.type}</span>
                      <span className={`lfv__tp-outcome ${good ? 'lfv__tp-outcome--good' : 'lfv__tp-outcome--bad'}`}>
                        {tp.outcome}
                      </span>
                      <span className="lfv__tp-time">{relativeDate(tp.touched_at)}</span>
                    </div>
                    {tp.notes && <div className="lfv__tp-notes">{tp.notes}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(lead.last_contacted_at || lead.next_follow_up_at) && (
        <div className="lfv__detail-dates">
          {lead.last_contacted_at && (
            <div className="lfv__detail-date-row">
              <span className="lfv__detail-date-lbl">Last contact</span>
              <span className="lfv__detail-date-val">{formatDate(lead.last_contacted_at)}</span>
            </div>
          )}
          {lead.next_follow_up_at && (
            <div className="lfv__detail-date-row">
              <span className="lfv__detail-date-lbl">Follow-up</span>
              <span className="lfv__detail-date-val">{formatDate(lead.next_follow_up_at)}</span>
            </div>
          )}
        </div>
      )}

      <div className="lfv__detail-actions">
        <button className="btn--primary" onClick={() => onLogClick(lead)}>Log Touchpoint</button>
        <button className="btn--sky" onClick={() => onCardClick(lead)}>Full Profile →</button>
      </div>
    </div>
  );
}

export default function LeadFocusView({ leads, onCardClick, onLogClick, lastRefreshed, filter, onFilterChange }) {
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    if (!selectedLead) return;
    const updated = leads.find(l => l._id === selectedLead._id);
    if (updated) setSelectedLead(updated);
  }, [leads]); // eslint-disable-line

  const activeLeads    = leads.filter(l => !TERMINAL_STATUSES.includes(l.status));
  const overdue        = activeLeads.filter(isOverdue);
  const dueToday       = activeLeads.filter(isDueToday);
  const hotNoTouch     = activeLeads.filter(isHotNoRecentTouch);
  const newUnactioned  = activeLeads.filter(isNewUnactioned);
  const totalActionable = overdue.length + dueToday.length + hotNoTouch.length + newUnactioned.length;

  function handleRowSelect(lead) {
    setSelectedLead(prev => prev && prev._id === lead._id ? null : lead);
  }

  function toggleFilter(key) {
    if (onFilterChange) onFilterChange(filter === key ? null : key);
  }

  const sections = [
    { key: 'overdue', leads: overdue,       label: 'Overdue',               mod: '--overdue' },
    { key: 'today',   leads: dueToday,      label: 'Due Today',             mod: '--today'   },
    { key: 'hot',     leads: hotNoTouch,    label: 'Hot — No Recent Touch', mod: '--hot'     },
    { key: 'new',     leads: newUnactioned, label: 'New — Unactioned',      mod: '--new'     },
  ];

  const visibleSections = filter
    ? sections.filter(s => s.key === filter)
    : sections.filter(s => s.leads.length > 0);

  if (totalActionable === 0) {
    return (
      <div className="lfv__all-clear">
        <div className="lfv__empty-icon">✓</div>
        <div className="lfv__empty-title">You're all caught up!</div>
        <div className="lfv__empty-sub">No actions needed right now.</div>
        {lastRefreshed && (
          <div className="lfv__refresh-ts">Updated {formatRefreshed(lastRefreshed)}</div>
        )}
      </div>
    );
  }

  return (
    <div className="lfv__pane">
      <div className="lfv__list">
        {lastRefreshed && (
          <div className="lfv__refresh-bar">Updated {formatRefreshed(lastRefreshed)}</div>
        )}

        {visibleSections.length === 0 && filter && (
          <div className="lfv__filter-empty">
            No leads in this category right now.{' '}
            <button className="lfv__filter-clear" onClick={() => onFilterChange && onFilterChange(null)}>
              Show all
            </button>
          </div>
        )}

        {visibleSections.map(sec => (
          <div key={sec.key} className="lfv__section">
            <button
              className={`lfv__section-header lfv__section-header${sec.mod} ${filter === sec.key ? 'lfv__section-header--filtered' : ''}`}
              onClick={() => toggleFilter(sec.key)}
            >
              <span className={`lfv__section-dot lfv__section-dot${sec.mod}`} />
              {sec.label}
              <span className="lfv__section-count">{sec.leads.length}</span>
              <span className="lfv__section-filter-tag">
                {filter === sec.key ? '✕ clear' : 'filter'}
              </span>
            </button>

            {sec.leads.map(lead => (
              <LeadFocusRow
                key={lead._id}
                lead={lead}
                selected={selectedLead && selectedLead._id === lead._id}
                onSelect={handleRowSelect}
                onLog={onLogClick}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="lfv__detail">
        {selectedLead ? (
          <DetailPanel lead={selectedLead} onLogClick={onLogClick} onCardClick={onCardClick} />
        ) : (
          <div className="lfv__placeholder">
            <div className="lfv__placeholder-icon">👈</div>
            <div className="lfv__placeholder-text">Select a lead to see their touchpoint history</div>
          </div>
        )}
      </div>
    </div>
  );
}
